/**
 * VIDEO CONVERTER - Server-side FFmpeg
 * Converts WebM to MP4 for universal compatibility (iPhone, WhatsApp, etc.)
 * Uses server-side FFmpeg for reliable conversion
 */

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:5000');

/**
 * Convert WebM/any video format to MP4 (H.264 + AAC)
 * Sends video to server for conversion using FFmpeg
 * Produces iPhone and WhatsApp compatible MP4 files
 *
 * @param {Blob} inputBlob - Input video blob (WebM, MP4, etc.)
 * @param {Object} options - Conversion options
 * @returns {Promise<Blob>} MP4 blob
 */
export const convertToMP4 = async (inputBlob, options = {}) => {
  const { onProgress = null } = options;

  console.log('🔄 Starting server-side video conversion to MP4...');
  console.log('📊 Input size:', (inputBlob.size / 1024 / 1024).toFixed(2), 'MB');

  try {
    // Show initial progress
    if (onProgress) onProgress(10);

    // Create FormData with the video file
    // Ensure blob has correct MIME type
    const videoBlob = new Blob([inputBlob], { type: 'video/webm' });
    const formData = new FormData();
    formData.append('video', videoBlob, 'video.webm');

    if (onProgress) onProgress(20);

    // Send to server for conversion
    console.log('📤 Uploading to server for conversion...');

    const response = await fetch(`${API_BASE_URL}/api/media/convert-to-mp4`, {
      method: 'POST',
      body: formData
    });

    if (onProgress) onProgress(60);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    if (onProgress) onProgress(80);

    // Get the converted MP4 blob
    const mp4Blob = await response.blob();

    console.log('✅ MP4 conversion complete!');
    console.log('📊 Output size:', (mp4Blob.size / 1024 / 1024).toFixed(2), 'MB');

    if (onProgress) onProgress(100);

    return mp4Blob;

  } catch (error) {
    console.error('❌ Video conversion error:', error);
    throw new Error(`Failed to convert video to MP4: ${error.message}`);
  }
};

/**
 * Check if server-side conversion is available
 * @returns {Promise<boolean>}
 */
export const isConversionAvailable = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    return response.ok;
  } catch {
    return false;
  }
};
